import { useMemo, type CSSProperties } from "react";
import type { NovelSummary } from "../apiClient.js";
import type { Page } from "../types.js";
import { FilmCard } from "../components/FilmCard.js";
import { ContinueReadingShelf } from "../components/ContinueReadingShelf.js";
import { pickLandingNovels } from "../lib/featuredNovels.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { isNovelFinished } from "../lib/storage.js";
import { followInApp, novelHref } from "../lib/links.js";
import { buildPath } from "../lib/route.js";
import { LANDING_FEATURES, LANDING_STEPS } from "./landingContent.js";

// Reader-first: the page is for someone who came to read. Creating a novel is
// a small link for authors (the route is unchanged — a signed-out visitor who
// follows it lands on the sign-up form, like the topbar's Create).
//
// The reader's ways on — Play now and the catalog — are real links (in-app on
// a plain click, lib/links.ts), so crawlers follow them too. The build also
// writes this page's fixed copy into index.html for crawlers that never run
// the app (prerender/staticPages.tsx, renderLandingStaticPage).

export function LandingPage({
  catalogNovels,
  catalogLoaded,
  onNavigate,
  onPlay,
  onOpenDetail,
  onOpenAuthor
}: {
  catalogNovels: NovelSummary[];
  catalogLoaded: boolean;
  onNavigate: (page: Page) => void;
  onPlay: (novelId: string) => void;
  onOpenDetail: (novelId: string) => void;
  /** Renders the catalog's author byline on every card when given. */
  onOpenAuthor?: (ownerId: string, username?: string) => void;
}) {
  const t = useT();
  const lang = useLanguage();
  // The hero features the first pinned novel this browser has not finished
  // (else an unfinished feed novel in the UI language's script —
  // lib/featuredNovels.ts); the showcase follows with the other pins, then the
  // feed's novels in the UI script, then the rest, never repeating the featured
  // one. catalogNovels is the public feed the App already fetched — no extra
  // request. Nothing is shown until it loads, so no placeholder flashes in
  // first. The finished marks are read with the list: the page remounts when
  // the reader comes back from the player.
  const selection = useMemo(
    () => pickLandingNovels(catalogNovels, { language: lang, isFinished: isNovelFinished }),
    [catalogNovels, lang]
  );
  // undefined = still loading, null = loaded with no novels to feature.
  const featured: NovelSummary | null | undefined = catalogLoaded ? selection.featured : undefined;
  const showcase = catalogLoaded ? selection.showcase : [];

  return (
    <div className="landing">
      <section className="hero">
        <div className="hero-copy">
          {/* The main heading says what the site is: the eyebrow line is part
              of it, styled as before. */}
          <h1>
            <span className="hero-eyebrow">{t("landing.eyebrow")}</span>{" "}
            <span className="hero-headline">{t("landing.heroTitle")}</span>
          </h1>
          <p>{t("landing.heroLead")}</p>
          <div className="button-row">
            {/* Play now opens the featured novel's player straight away. It
                waits (a disabled button) while the catalog loads, and steps
                aside when there is nothing to play. */}
            {featured === undefined && (
              <button type="button" disabled>
                {t("landing.playNow")}
              </button>
            )}
            {featured && (
              <a
                className="button-link"
                href={novelHref("player", featured)}
                aria-label={t("landing.playNowTitle", { title: featured.title })}
                onClick={(event) => followInApp(event, () => onPlay(featured.id))}
              >
                {t("landing.playNow")}
              </a>
            )}
            <CatalogLink
              className={featured === null ? "button-link" : "button-link secondary"}
              label={t("landing.browseCatalog")}
              onNavigate={onNavigate}
            />
          </div>
          <p className="hero-authors">
            {t("landing.authorsPrompt")}{" "}
            <button type="button" className="text-link" onClick={() => onNavigate("create")}>
              {t("landing.authorsLink")}
            </button>
          </p>
        </div>
        {featured ? (
          <div className="hero-feature">
            <p className="hero-feature-label">★ {t("landing.featuredLabel")}</p>
            <div className="hero-feature-card">
              <FilmCard
                novel={featured}
                lang={lang}
                t={t}
                onPlay={onPlay}
                onOpenDetail={onOpenDetail}
                onOpenAuthor={onOpenAuthor}
                priority
              />
            </div>
          </div>
        ) : featured === undefined ? (
          <div className="hero-feature" aria-hidden="true">
            <p className="hero-feature-label">★ {t("landing.featuredLabel")}</p>
            <div className="hero-feature-card">
              <div className="film-card film-card--skeleton skeleton" />
            </div>
          </div>
        ) : null}
      </section>

      {/* A returning reader's unfinished stories, before anything new. */}
      {catalogLoaded && (
        <ContinueReadingShelf novels={catalogNovels} onPlay={onPlay} className="page-section landing-continue" />
      )}

      {showcase.length > 0 && (
        <section className="page-section" aria-labelledby="landing-showcase-heading">
          <div className="landing-showcase-heading">
            <div className="landing-heading landing-heading--tight">
              <p className="eyebrow">{t("landing.showcaseEyebrow")}</p>
              <h2 id="landing-showcase-heading">{t("landing.showcaseHeading")}</h2>
            </div>
            <CatalogLink className="button-link secondary" label={t("landing.browseAll")} onNavigate={onNavigate} />
          </div>
          <div
            className="film-grid landing-showcase-grid"
            style={{ "--showcase-count": showcase.length } as CSSProperties}
          >
            {showcase.map((novel) => (
              <FilmCard
                key={novel.id}
                novel={novel}
                lang={lang}
                t={t}
                onPlay={onPlay}
                onOpenDetail={onOpenDetail}
                onOpenAuthor={onOpenAuthor}
              />
            ))}
          </div>
        </section>
      )}

      <section className="page-section" aria-labelledby="landing-how-heading">
        <div className="landing-heading">
          <p className="eyebrow">{t("landing.howEyebrow")}</p>
          <h2 id="landing-how-heading">{t("landing.howHeading")}</h2>
        </div>
        <ol className="landing-steps">
          {LANDING_STEPS.map((step, index) => (
            <li key={step.titleKey} className="landing-tile">
              <span className="landing-step-num">0{index + 1}</span>
              <h3>{t(step.titleKey)}</h3>
              <p>{t(step.textKey)}</p>
            </li>
          ))}
        </ol>
        <p className="landing-authors-note">
          {t("landing.authorsNote")}{" "}
          <button type="button" className="text-link" onClick={() => onNavigate("create")}>
            {t("landing.authorsLink")}
          </button>
        </p>
      </section>

      <section className="landing-features-band" aria-labelledby="landing-features-heading">
        <div className="page-section">
          <div className="landing-heading">
            <p className="eyebrow">{t("landing.builtEyebrow")}</p>
            <h2 id="landing-features-heading">{t("landing.builtHeading")}</h2>
          </div>
          <div className="landing-features">
            {LANDING_FEATURES.map((feature) => (
              <article key={feature.titleKey} className="landing-tile">
                <span className="landing-feature-icon">{feature.icon}</span>
                <h3>{t(feature.titleKey)}</h3>
                <p>{t(feature.textKey)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta" aria-labelledby="landing-cta-heading">
        <h2 id="landing-cta-heading">{t("landing.ctaHeading")}</h2>
        <p>{t("landing.ctaText")}</p>
        <CatalogLink className="button-link" label={t("landing.browseCatalog")} onNavigate={onNavigate} />
      </section>
    </div>
  );
}

/** A button-styled link to /catalog that opens it in-app on a plain click. */
function CatalogLink({
  className,
  label,
  onNavigate
}: {
  className: string;
  label: string;
  onNavigate: (page: Page) => void;
}) {
  return (
    <a className={className} href={buildPath("catalog", null)} onClick={(event) => followInApp(event, () => onNavigate("catalog"))}>
      {label}
    </a>
  );
}
